import { useState } from 'react';

import { useMcpToolCatalog } from '@commenda/api';
import {
  Badge,
  Button,
  FieldWrapper,
  Icons,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToggle,
} from '@commenda/kakashi';
import { TMapping, TSysToolCallStepDefinition } from '@commenda/types';

import { useWorkflow } from '../../context/WorkflowContext';
import { WorkflowToolCallHelper } from '../../helpers';
import { TSelectedEntity } from '../../types/node';
import { MappingModal } from '../modals/AddEditMappingModal';

interface ToolCallNodePropertyPanelProps {
  selectedEntity: TSelectedEntity;
}

export const ToolCallNodePropertyPanel = ({
  selectedEntity,
}: ToolCallNodePropertyPanelProps) => {
  const { workflow, setWorkflow } = useWorkflow();
  const mappingModalVisibility = useToggle(false);
  const [mappingToEdit, setMappingToEdit] = useState<TMapping | null>(null);

  const { tools, error, isLoading, mutate } = useMcpToolCatalog();

  // Safe: NodePropertyPanel only renders this panel when the selected step's type is SYS_TOOL_CALL.
  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TSysToolCallStepDefinition | undefined;

  if (!selectedStep) return <div>Step not found</div>;

  const updateStep = (updates: Partial<TSysToolCallStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const selectedTool = tools?.find(
    (tool) => tool.name === selectedStep.toolName
  );
  const inputMapping = selectedStep.inputMapping ?? [];
  const toolParameters = selectedTool?.parameters ?? [];
  const alwaysRequired =
    WorkflowToolCallHelper.findAlwaysRequiredArguments(toolParameters);
  const missingArgs = WorkflowToolCallHelper.findMissingArguments(
    toolParameters,
    inputMapping
  );

  const onSaveMapping = (mapping: TMapping) => {
    const existing = inputMapping.find((m) => m.id === mapping.id);
    updateStep({
      inputMapping: existing
        ? inputMapping.map((m) => (m.id === mapping.id ? mapping : m))
        : [...inputMapping, mapping],
    });
  };

  return (
    <div className="flex flex-col gap-xl">
      <FieldWrapper>
        <Label>Tool</Label>
        {error ? (
          <div className="flex items-center justify-between gap-md">
            <p className="txt-extra-small-regular text-text-error">
              Could not load the tool list.
            </p>
            <Button variant="tertiary" size="sm" onClick={() => mutate()}>
              Retry
            </Button>
          </div>
        ) : (
          <Select
            value={selectedStep.toolName || undefined}
            onValueChange={(toolName) => {
              // The previous tool's mappings target parameters the new one may not have.
              updateStep({ toolName, inputMapping: [] });
            }}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={isLoading ? 'Loading tools…' : 'Select a tool'}
              />
            </SelectTrigger>
            <SelectContent>
              {tools?.map((tool) => (
                <SelectItem key={tool.name} value={tool.name}>
                  {tool.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {selectedTool && (
          <>
            <details className="mt-xs">
              <summary className="txt-extra-small-regular text-text-secondary cursor-pointer">
                What this tool does
              </summary>
              <p className="txt-extra-small-regular text-text-secondary mt-xs">
                {selectedTool.description}
              </p>
            </details>
            <div className="flex gap-xs mt-xs">
              <Badge
                variant={selectedTool.readOnly ? 'gray' : 'warning'}
                size="sm"
              >
                {selectedTool.readOnly ? 'Reads data' : 'Changes data'}
              </Badge>
              {selectedTool.async && (
                <Badge variant="gray" size="sm">
                  Starts a background job
                </Badge>
              )}
            </div>
          </>
        )}

        {!selectedStep.toolName && !isLoading && !error && (
          <p className="txt-extra-small-regular text-text-error mt-xs">
            Select the tool this step should call.
          </p>
        )}

        {/* The tool's own declaration drives the watch, so the author neither adds nor configures it. */}
        {selectedTool?.async && (
          <p className="txt-extra-small-regular text-text-secondary mt-xs">
            A &quot;Wait for the job to finish&quot; step is added after this
            one automatically. Connect this step to whatever should run once the
            job completes.
          </p>
        )}
      </FieldWrapper>

      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span className="grow">Arguments</span>
          <Button
            size="sm"
            disabled={!selectedStep.toolName}
            onClick={() => {
              setMappingToEdit(null);
              mappingModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>

        {inputMapping.length === 0 ? (
          <p className="txt-extra-small-regular text-text-secondary">
            {selectedStep.toolName
              ? 'No arguments mapped yet.'
              : 'Select a tool first to see which arguments it takes.'}
          </p>
        ) : (
          <div className="flex flex-col gap-xs">
            {inputMapping.map((mapping) => (
              <div
                key={mapping.id}
                className="flex items-center justify-between gap-md"
              >
                <span className="txt-small-regular text-text-primary">
                  {mapping.targetPath}
                </span>
                <div className="flex items-center gap-xs">
                  <Icons.Edit01
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      setMappingToEdit(mapping);
                      mappingModalVisibility.show();
                    }}
                  />
                  <Icons.Trash02
                    className="size-4 cursor-pointer"
                    onClick={() =>
                      updateStep({
                        inputMapping: inputMapping.filter(
                          (m) => m.id !== mapping.id
                        ),
                      })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {missingArgs.length > 0 && (
          <p className="txt-extra-small-regular text-text-error mt-xs">
            Missing required argument: {missingArgs.join(', ')}
          </p>
        )}

        {selectedTool && (
          <p className="txt-extra-small-regular text-text-secondary mt-xs">
            This tool takes:{' '}
            {WorkflowToolCallHelper.describeArguments(toolParameters)}
            {alwaysRequired.length > 0 && (
              <> — always required: {alwaysRequired.join(', ')}</>
            )}
            . Some tools require one of several arguments; check &ldquo;What
            this tool does&rdquo; above for that. Map the company or entity id
            from the run&apos;s <code>corporationId</code>.
          </p>
        )}
      </FieldWrapper>

      {workflow && (
        <MappingModal
          isOpen={mappingModalVisibility.visible}
          onClose={() => {
            mappingModalVisibility.hide();
            setMappingToEdit(null);
          }}
          mapping={mappingToEdit}
          onMappingChange={onSaveMapping}
          workflowDefinition={workflow}
          currentStepId={selectedEntity.id}
        />
      )}
    </div>
  );
};
